/** 
 * @module Webreader
 * @description Webreader module. Present the webreader in a web page with the given issue URL and options.
*/

import { createApp, reactive, configureCompat } from 'vue'
import { createMemoryHistory, createWebHashHistory, createRouter } from 'vue-router'
import Vue3TouchEvents from 'vue3-touch-events'
import Vue3SocialSharingPlugin from 'vue3-social-sharing'
import axios from 'axios'
import NProgress from 'nprogress'
import WebreaderApp from './WebreaderApp.vue'
import i18n from './lang/i18n'
import store from './store.js'
import CciEventTrackingPlugin from './tracking/CciEventTrackingPlugin'

const CciPages = () => import(/* webpackChunkName: 'Pages' */ /* webpackPrefetch: true */  'components/ccipages/CciPages.vue')
const CciArticleContainer = () => import(/* webpackChunkName: 'ArticleContainer' */  /* webpackPrefetch: true */ 'components/cciarticlecontainer/CciArticleContainer.vue')
const CciArticleListMobile = () => import( /* webpackChunkName: 'ArticleListMobile' */ /* webpackPrefetch: true */ 'components/cciarticlelistmobile/CciArticleListMobile.vue')
const CciArticle = () => import( /* webpackChunkName: 'Article' */ /* webpackPrefetch: true */ 'components/cciarticle/CciArticle.vue')
const CciError = () => import( /* webpackChunkName: 'Error' */ './CciError.vue')
import CciLoader from './CciLoader.vue'

/* eslint-disable no-new */
/* eslint-disable no-unused-vars */

import { IssueData } from 'issueData';

// disable compat for certain features
configureCompat({
  ATTR_FALSE_VALUE: false,
  MODE: 3
})

let vueApp = null



/**
 * @typedef {Object} Options
 * @property {boolean} [enableArticleSocialSharing=true] - Enable social sharing of articles.
 * @property {boolean} [enableFullscreen=true] - Enable fullscreen mode.
 * @property {boolean} [enableFontSizeChange=true] - Enable font size change.
 * @property {boolean} [enableArticlePrinting=true] - Enable printing of articles.
 * @property {boolean} [enableDownloadOfArticleImage=true] - Enable download of article image.
 * @property {boolean} [enableDownloadOfPages=true] - Enable download of pages.
 * @property {boolean} [enablePrintOfPages=true] - Enable print of pages.
 * @property {boolean} [enablePrintOfCurrentPage=false] - Enable print of current page.
 * @property {boolean} [enablePrintDownloadConsent=false] - Enable print/download consent.
 * @property {boolean} [enableArticleList=true] - Enable article list.
 * @property {boolean} [enableHomeNavigation=true] - Enable home navigation.
 * @property {boolean} [enableIssueOverview=true] - Enable issue overview.
 * @property {boolean} [enableCropPage=true] - Enable crop page.
 * @property {boolean} [enableSectionsBar=true] - Enable sections bar.
 * @property {boolean} [enableScrubber=true] - Enable scrubber.
 * @property {boolean} [enablePageViewModeChange=true] - Enable page view mode change.
 * @property {boolean} [enableArticlesWithTablesAsImages=false] - Enable articles with tables as images. This may be required for some articles with complex table layouts.
 * @property {boolean} [enablePageAds=true] - Enable page ads. The ads on the pages will be clickable and will open in an overlay dialog.
 * @property {boolean} [enableZoomButtonsForMobile=false] - Enable page zoom buttons for mobile.
 * @property {boolean} [enableOnlineURLArticles=false] - Enable online URL articles.
 * @property {boolean} [enableInteractionLayerHoverEffect=true] - Enable interaction layer hover effect.
 * @property {boolean} [enableArticleFavourite=false] - Enable article favourite.
 * @property {boolean} [enablePagePreviewInArticleList=true] - Enable page preview in article list.
 * @property {boolean} [enablePageNumbersInArticleList=true] - Enable page numbers in article list.
 * @property {boolean} [enableSectionPageNumbering=false] - Enable section page numbering.
 * @property {boolean} [enableVerticalLayout=false] - Enable vertical layout.
 * @property {boolean} [enableVerticalLayoutForMobile=false] - Enable vertical layout for mobile.
 * @property {boolean} [enableArticleViewModeChange=true] - Enable article view mode change.
 * @property {boolean} [enableUniquePageGroups=true] - Enable unique page groups.
 * @property {boolean} [enableArticleAudioAutoplayNext=true] - Enable article audio autoplay next.
 * @property {boolean} [enablePDFTextLayerForDesktop=false] - Enable PDF text layer for desktop.
 * @property {boolean} [enableArticles=true] - Enable articles.
 * @property {boolean} [enableArticleNavigation=true] - Enable navigation to next/previous article in article view.
 * @property {boolean} [enableInterstitialAds=false] - Enable interstitial ads. Requires interstitialAdFunctions.
 * @property {boolean} [enableSinglePagesModeSwitch=true] - Enable switching between single and double pages mode.
 * @property {number} [maxZoom=6.0] - Maximum zoom level.
 * @property {number} [doubleClickZoomIncrease=3.0] - Double click zoom increase.
 * @property {string} [navigationBarTitle=""] - Navigation bar title.
 * @property {string} [homeNavigationUrl=""] - Home navigation URL.
 * @property {"print"|"online"|"reader"|"custom"} [articleSharingType="print"] - Article sharing type.
 * @property {"text"|"image"} [articleViewModeDefault="text"] - Article view mode default.
 * @property {boolean} [singlePagesModeDefault=false] - Single pages mode default.
 * @property {"en"|"de"|"fr"|"es"} [language="en"] - Language code.
 * @property {boolean} [isFullPageViewMode=true] - Full page view mode.
 * @property {number} [pagePDFQuality=2.0] - Page PDF rendering quality when zooming. Higher values give better quality but use more memory.
 * @property {number} [pageCropScale=4] - Page crop scale.
 * @property {number} [startPage=1] - Start page. Priority below startArticleId.
 * @property {string} [startArticleId=""] - Start article or ad ID. Priority over startPage.
 * @property {boolean} [useBitmapPagesForDesktop=false] - Use bitmap pages for desktop.
 * @property {boolean} [useBitmapPagesForMobile=true] - Use bitmap pages for mobile devices.
 * @property {"hash"|"abstract"} [routerMode="hash"] - Router mode.
 * @property {function(Object): string} [customArticleShareURLFunction] - Custom article share URL function.
 * @property {InterstitialAdFunctions} [interstitialAdFunctions] - Interstitial ad functions.
 */

/**
 * @typedef {Object} Messages
 * @description All translatable UI strings for the Webreader.
 * @property {string} [cci_ad_main_aria_label="Ad content view"] - ARIA label for the ad content view.
 * @property {string} [cci_article_audio_player_aria_label="Article audio player"] - ARIA label for the article audio player.
 * @property {string} [cci_article_close_modal_title="Close article view dialog"] - Title for the button to close the article view dialog.
 * @property {string} [cci_article_img_view_ad_aria_description="Click to display ad in dialog"] - ARIA description for displaying the ad in a dialog.
 * @property {string} [cci_article_img_view_article_aria_description="Click to display article image in dialog"] - ARIA description for displaying the article image in a dialog.
 * @property {string} [cci_article_list_aria_label="List of articles"] - ARIA label for the list of articles.
 * @property {string} [cci_article_list_item_aria_label="Article list item with title: HEADLINE. From page PAGENUMBER"] - ARIA label for an article list item.
 * @property {string} [cci_article_list_page_label="Pg PAGENUMBER"] - Label for the page in the article list.
 * @property {string} [cci_article_list_page_thumbnail_aria_label="Link to page PAGENUMBER"] - ARIA label for the page thumbnail link.
 * @property {string} [cci_article_list_section_aria_label="SECTION section"] - ARIA label for a section in the article list.
 * @property {string} [cci_article_list_section_items_aria_label="List of articles in section SECTION"] - ARIA label for articles in a section.
 * @property {string} [cci_article_loading_aria_label="Loading article..."] - ARIA label for loading state in articles.
 * @property {string} [cci_article_main_aria_label="Article content view"] - ARIA label for the article content view.
 * @property {string} [cci_article_modal_ad_aria_label="Ad view dialog. Press Escape to close."] - ARIA label for the ad view dialog.
 * @property {string} [cci_article_modal_article_aria_label="Article view dialog. Press Escape to close."] - ARIA label for the article view dialog.
 * @property {string} [cci_article_modal_header_text_page_label="Page PAGENUMBER"] - Label for the page in the article modal header.
 * @property {string} [cci_article_next="Navigate to next article"] - Label for the button to navigate to the next article.
 * @property {string} [cci_article_prev="Navigate to previous article"] - Label for the button to navigate to the previous article.
 * @property {string} [cci_confirmation_cancel="Cancel"] - Label for the cancel button.
 * @property {string} [cci_confirmation_comfirm="Yes, I'm sure"] - Label for the confirm button.
 * @property {string} [cci_crop_page_cancel_btn_title="Cancel"] - Title for the cancel button in the crop page dialog.
 * @property {string} [cci_crop_page_done_btn_title="Crop"] - Title for the crop button in the crop page dialog.
 * @property {string} [cci_download_confirmation_check_box_1_label="I understand that this is copyrighted material meant only for my personal use."] - Label for the first download confirmation checkbox.
 * @property {string} [cci_download_confirmation_check_box_2_label="I agree not to distribute this content on public platforms without permission."] - Label for the second download confirmation checkbox.
 * @property {string} [cci_download_confirmation_dialog_text="Are you sure you want to download the entire publication?"] - Text for the download confirmation dialog.
 * @property {string} [cci_download_confirmation_title_full="Are you sure you want to download the full edition?"] - Title for downloading the full edition.
 * @property {string} [cci_download_confirmation_title_part="Are you sure you want to download the selected page(s)?"] - Title for downloading selected pages.
 * @property {string} [cci_fav_article_add_text="Add article to favourites"] - Text for adding an article to favourites.
 * @property {string} [cci_fav_article_remove_text="Remove article from favourites"] - Text for removing an article from favourites.
 * @property {string} [cci_image_gallery_caption_title="Image caption"] - Title for the image caption.
 * @property {string} [cci_image_gallery_close_modal_title="Close image gallery dialog"] - Title for the button to close the image gallery dialog.
 * @property {string} [cci_image_gallery_image_alt_ad="Advertisement image"] - Alt text for an advertisement image.
 * @property {string} [cci_image_gallery_image_alt_article="Article image CURRENTNUMBER of TOTALNUMBER"] - Alt text for an article image with current and total numbers.
 * @property {string} [cci_image_gallery_modal_aria_label="Image gallery dialog. Press Escape to close."] - ARIA label for the image gallery dialog.
 * @property {string} [cci_image_gallery_next_image="Navigate to next image"] - Label for the button to navigate to the next image.
 * @property {string} [cci_image_gallery_prev_image="Navigate to previous image"] - Label for the button to navigate to the previous image.
 * @property {string} [cci_issue_overview_close_modal_title="Close issue overview dialog"] - Title for the button to close the issue overview dialog.
 * @property {string} [cci_issue_overview_download_aria_description="Download selected pages PAGENUMBERS"] - ARIA description for downloading selected pages.
 * @property {string} [cci_issue_overview_download_btn_text="Download"] - Text for the download button.
 * @property {string} [cci_issue_overview_download_btn_title="Download pages"] - Title for the download pages button.
 * @property {string} [cci_issue_overview_item_aria_label="Page PAGENUMBER"] - ARIA label for a page in the issue overview.
 * @property {string} [cci_issue_overview_item_img_alt="Page PAGENUMBER"] - Alt text for a page image in the issue overview.
 * @property {string} [cci_issue_overview_item_nav_aria_description="Click to navigate to page"] - ARIA description for navigating to a page.
 * @property {string} [cci_issue_overview_item_sel_aria_description="Click to select page"] - ARIA description for selecting a page.
 * @property {string} [cci_issue_overview_modal_aria_label="Issue overview dialog. Press Escape to close."] - ARIA label for the issue overview dialog.
 * @property {string} [cci_issue_overview_modal_heading="Issue overview dialog"] - Heading for the
 * @property {string} [cci_issue_overview_print_aria_description="Print selected pages PAGENUMBERS"] - ARIA description for printing selected pages.
 * @property {string} [cci_issue_overview_print_btn_text="Print"] - Text for the print button.
 * @property {string} [cci_issue_overview_print_btn_title="Print pages"] - Title for the print pages button.
 * @property {string} [cci_issue_overview_section_aria_label="SECTION section PAGENUMBER to NEXTPAGENUMBER"] - ARIA label for a section in the issue overview.
 * @property {string} [cci_issue_overview_section_label="Page PAGENUMBERS"] - Label for a section in the issue overview.
 * @property {string} [cci_issue_overview_section_sel_aria_description="Click to select pages in section"] - ARIA description for selecting pages in a section.
 * @property {string} [cci_main_heading="Digital publication"] - Main heading for the webreader.
 * @property {string} [cci_nav_bar_aria_label="Navigation and tool bar"] - ARIA label for the navigation and tool bar.
 * @property {string} [cci_nav_bar_article_list_title="Go to article list"] - Title for the button to go to the article list.
 * @property {string} [cci_nav_bar_burger_menu_title_close="Close toolbar"] - Title for the button to close the toolbar.
 * @property {string} [cci_nav_bar_burger_menu_title_open="Open toolbar"] - Title for the button to open the toolbar.
 * @property {string} [cci_nav_bar_crop_button_select_title="Select page to crop"] - Title for the button to select a page to crop.
 * @property {string} [cci_nav_bar_crop_button_title="Crop page"] - Title for the button to crop the current page.
 * @property {string} [cci_nav_bar_decrease_font_title="Decrease font size"] - Title for the button to decrease font size.
 * @property {string} [cci_nav_bar_double_pages_mode_switch_title="Switch to double pages mode"] - Title for the button to switch to double pages mode.
 * @property {string} [cci_nav_bar_download_article_image_title="Download article image"] - Title for the button to download the article image.
 * @property {string} [cci_nav_bar_download_title="Open download pages dialog"] - Title for the button to open the download pages dialog.
 * @property {string} [cci_nav_bar_faq_icon_title="FAQ"] - Title for the FAQ icon.
 * @property {string} [cci_nav_bar_full_screen_view_title="View pages in full screen mode"] - Title for the button to view pages in full screen mode.
 * @property {string} [cci_nav_bar_home_title="Go to previous issues"] - Title for the button to go to previous issues.
 * @property {string} [cci_nav_bar_increase_font_title="Increase font size"] - Title for the button to increase font size.
 * @property {string} [cci_nav_bar_page_view_title="Go to pages"] - Title for the button to go to pages.
 * @property {string} [cci_nav_bar_play_article_title="Play article audio"] - Title for the button to play article audio.
 * @property {string} [cci_nav_bar_print_ad_title="Print ad"] - Title for the button to print the ad.
 * @property {string} [cci_nav_bar_print_article_title="Print article"] - Title for the button to print the article.
 * @property {string} [cci_nav_bar_print_current_page_select_title="Select page to print"] - Title for the button to select a page to print.
 * @property {string} [cci_nav_bar_print_current_page_title="Print page"] - Title for the button to print the current page.
 * @property {string} [cci_nav_bar_print_title="Open print pages dialog"] - Title for the button to open the print pages dialog.
 * @property {string} [cci_nav_bar_select_image_view="Display article as image"] - Label for displaying the article as an image.
 * @property {string} [cci_nav_bar_select_left_page_title="Left page"] - Title for the button to select the left page.
 * @property {string} [cci_nav_bar_select_right_page_title="Right page"] - Title for the button to select the right page.
 * @property {string} [cci_nav_bar_select_text_view="Display article as text"] - Label for displaying the article as text.
 * @property {string} [cci_nav_bar_show_issue_overview_title="Open issue overview dialog"] - Title for the button to open the issue overview dialog.
 * @property {string} [cci_nav_bar_show_scrubber_title="Open page navigation scrubber"] - Title for the button to open the page navigation scrubber.
 * @property {string} [cci_nav_bar_single_pages_mode_switch_title="Switch to single pages mode"] - Title for the button to switch to single pages mode.
 * @property {string} [cci_nav_bar_social_sharing_title="Social sharing"] - Title for the button to open social sharing.
 * @property {string} [cci_nav_bar_stop_article_title="Stop article audio"] - Title for the button to stop article audio.
 * @property {string} [cci_nav_bar_title="Navigation and tool bar"] - Title for the navigation and tool bar.
 * @property {string} [cci_nav_bar_toggle_page_view_mode_full_title="Fit to full page"] - Title for the button to fit to full page.
 * @property {string} [cci_nav_bar_toggle_page_view_mode_width_title="Fit to page width"] - Title for the button to fit to page width.
 * @property {string} [cci_nav_bar_zoom_in_title="Zoom in on page"] - Title for the button to zoom in on the page.
 * @property {string} [cci_nav_bar_zoom_out_title="Zoom out on page"] - Title for the button to zoom out on the page.
 * @property {string} [cci_page_ad_area_aria_description="Click to display ad"] - ARIA description for displaying an ad.
 * @property {string} [cci_page_ad_area_title="Ad"] - Title for an ad on a page.
 * @property {string} [cci_page_article_aria_description="Click to open the article"] - ARIA description for opening the article.
 * @property {string} [cci_page_article_title="Article with title:"] - Title for an article on a page.
 * @property {string} [cci_page_bitmap_alt_text="Page PAGENUMBER"] - Alt text for bitmap page images.
 * @property {string} [cci_page_clickable_ad_aria_description="Click to open link in new tab"] - ARIA description for opening a link in a new tab.
 * @property {string} [cci_page_double_announce="Pages PAGENUMBER and NEXTPAGENUMBER"] - Announcement for double page mode.
 * @property {string} [cci_page_next="Navigate to next page"] - Label for the button to navigate to the next page.
 * @property {string} [cci_page_prev="Navigate to previous page"] - Label for the button to navigate to the previous page.
 * @property {string} [cci_page_single_announce="Page PAGENUMBER"] - Announcement for single page mode.
 * @property {string} [cci_page_thumbnail_alt_text="Thumbnail of page PAGENUMBER"] - Alt text for page thumbnails.
 * @property {string} [cci_pages_container_aria_label="Pages navigation and content"] - ARIA label for the pages navigation and content.
 * @property {string} [cci_print_confirmation_check_box_1_label="I understand that this is copyrighted material meant only for my personal use."] - Label for the first print confirmation checkbox.
 * @property {string} [cci_print_confirmation_check_box_2_label="I agree not to distribute this content on public platforms without permission."] - Label for the second print confirmation checkbox.
 * @property {string} [cci_print_confirmation_dialog_text="Are you sure you want to print the entire publication?"] - Text for the print confirmation dialog.
 * @property {string} [cci_print_confirmation_title_full="Are you sure you want to open the full edition for printing?"] - Title for printing the full edition.
 * @property {string} [cci_print_confirmation_title_part="Are you sure you want to open the selected page(s) for printing?"] - Title for printing selected pages.
 * @property {string} [cci_scrubber_item_aria_description="Click to navigate to the selected page"] - ARIA description for navigating to the selected page.
 * @property {string} [cci_scrubber_item_title="Page"] - Title for a page in the scrubber.
 * @property {string} [cci_scrubber_next="Scroll scrubber to the right"] - Label for scrolling the scrubber to the right.
 * @property {string} [cci_scrubber_prev="Scroll scrubber to the left"] - Label for scrolling the scrubber to the left.
 * @property {string} [cci_scrubber_title="Scrubber for navigation to pages"] - Title for the page navigation scrubber.
 * @property {string} [cci_section_bar_title="Sections navigation bar"] - Title for the sections navigation bar.
 * @property {string} [cci_section_item_aria_description="Click to navigate to the selected section"] - ARIA description for navigating to the selected section.
 * @property {string} [cci_section_nav_next="Scroll section bar to the right"] - Label for scrolling the section bar to the right.
 * @property {string} [cci_section_nav_prev="Scroll section bar to the left"] - Label for scrolling the section bar to the left.
 * @property {string} [cci_skip_link_article_list="Skip article list"] - Label for the skip to article list link.
 * @property {string} [cci_skip_link_main="Skip to main content"] - Label for the skip to main content link.
 * @property {string} [cci_skip_link_sections_bar="Skip to page sections bar"] - Label for the skip to sections bar link.
 * @property {string} [cci_social_sharing_close_modal_title="Close share article dialog"] - Title for the button to close the share article dialog.
 * @property {string} [cci_social_sharing_copy_link_message="Copied!"] - Message shown when a link is copied.
 * @property {string} [cci_social_sharing_copy_link_type="Copy"] - Label for the copy link button.
 * @property {string} [cci_social_sharing_modal_aria_label="Share article dialog. Press Escape to close."] - ARIA label for the share article dialog.
 * @property {string} [cci_social_sharing_modal_title="Share the article"] - Title for the social sharing modal.
 */


/**
 * @typedef {Object} InterstitialAdFunctions
 * @property {function(number, Array<string>, Array<string>, string): InterstitialAdPlacements} wantInterstitialAd - Determines if an interstitial ad should be shown. Returns placements.
 * @property {function(string): void} displayInterstitialAd - Displays the interstitial ad in the given container.
 * @property {function(string, number, number, Array<string>): void} interstitialAdPageIsReady - Called when the interstitial ad page is ready.
 * @property {function(string): void} interstitialAdPageWillBeDestroyed - Called before the interstitial ad page is destroyed.
 */

/**
 * @typedef {Object} EventContext
 * @property {"PageView"} PageView - Page view context.
 * @property {"ArticleView"} ArticleView - Article view context.
 * @property {"ArticleList"} ArticleList - Article list view context.
 * @property {"CropView"} CropView - Crop view context.
 * @property {"IssueOverviewView"} IssueOverviewView - Issue overview view context.
 * @property {"ScrubberView"} ScrubberView - Scrubber view context.
 * @property {"PrintDownloadOverview"} PrintDownloadOverview - Print or download overview context.
 * @property {"AdView"} AdView - Ad view context.
 */

/**
 * @typedef {Object} EventAction
 * @property {"AllActions"} AllActions - All user actions. This is a special action that can be used to track all user actions.
 * @property {"IssueLoaded"} IssueLoaded - Webreader app - Issue data loaded
 * @property {"PagesScrubberButtonClick"} PagesScrubberButtonClick - Pages - Scrubber button click
 * @property {"PagesFullscreenModeOnButtonClick"} PagesFullscreenModeOnButtonClick - Pages - Fullscreen mode on button click
 * @property {"PagesFullscreenModeOffButtonClick"} PagesFullscreenModeOffButtonClick - Pages - Fullscreen mode off button click
 * @property {"PagesPageView"} PagesPageView - Pages - Page view
 * @property {"PagesInterstitialPageView"} PagesInterstitialPageView - Pages - Interstitial page view
 * @property {"PagesClickableAd"} PagesClickableAd - Pages - Ad click on page
 * @property {"PagesPageZoomDoubleClickOut"} PagesPageZoomDoubleClickOut - Pages - Zoom out double click
 * @property {"PagesPageZoomDoubleClickIn"} PagesPageZoomDoubleClickIn - Pages - Zoom in double click
 * @property {"PagesPageZoomTouchPinchOut"} PagesPageZoomTouchPinchOut - Pages - Zoom out touch pinch
 * @property {"PagesPageZoomTouchPinchIn"} PagesPageZoomTouchPinchIn - Pages - Zoom in touch pinch
 * @property {"PagesPageZoomTrackpadPinchOut"} PagesPageZoomTrackpadPinchOut - Pages - Zoom out trackpad pinch
 * @property {"PagesPageZoomTrackpadPinchIn"} PagesPageZoomTrackpadPinchIn - Pages - Zoom in trackpad pinch
 * @property {"PagesPageZoomButtonClickOut"} PagesPageZoomButtonClickOut - Pages - Zoom out button click
 * @property {"PagesPageZoomButtonClickIn"} PagesPageZoomButtonClickIn - Pages - Zoom in button click
 * @property {"PagesCropButtonClick"} PagesCropButtonClick - Pages - Crop button click
 * @property {"PagesIssueOverviewButtonClick"} PagesIssueOverviewButtonClick - Pages - Issue overview button click
 * @property {"PagesIssueDownloadButtonClick"} PagesIssueDownloadButtonClick - Pages - Download issue button click
 * @property {"PagesIssuePrintButtonClick"} PagesIssuePrintButtonClick - Pages - Print issue button click
 * @property {"PagesArticleListButtonClick"} PagesArticleListButtonClick - Pages - Article list button click
 * @property {"PagesGroupNavigationSelect"} PagesGroupNavigationSelect - Pages - Group navigation bar select group
 * @property {"PagesPrintCurrentPageButtonClick"} PagesPrintCurrentPageButtonClick - Pages - Print current page button click
 * @property {"PagesSinglePagesModeButtonClick"} PagesSinglePagesModeButtonClick - Pages - Single pages mode button click
 * @property {"PagesDoublePagesModeButtonClick"} PagesDoublePagesModeButtonClick - Pages - Double pages mode button click
 * @property {"PagesArticleClick"} PagesArticleClick - Pages - Article click
 * @property {"PagesSwipe"} PagesSwipe - Pages - Swipe
 * @property {"CropOkButtonClick"} CropOkButtonClick - Crop - OK button click
 * @property {"CropCancelButtonClick"} CropCancelButtonClick - Crop - Cancel button click
 * @property {"ArticleView"} ArticleView - Article - Article view
 * @property {"ArticleShareButtonClick"} ArticleShareButtonClick - Article - Share button click
 * @property {"ArticleShareOpened"} ArticleShareOpened - Article - Share opened
 * @property {"ArticleIncreaseFontSizeButtonClick"} ArticleIncreaseFontSizeButtonClick - Article - Increase font size button click
 * @property {"ArticleDecreaseFontSizeButtonClick"} ArticleDecreaseFontSizeButtonClick - Article - Decrease font size button click
 * @property {"ArticlePrintButtonClick"} ArticlePrintButtonClick - Article - Print button click
 * @property {"ArticleAddToFavouritesButtonClick"} ArticleAddToFavouritesButtonClick - Article - Add to favourites button click
 * @property {"ArticleRemoveFromFavouritesButtonClick"} ArticleRemoveFromFavouritesButtonClick - Article - Remove from favourites button click
 * @property {"ArticleClose"} ArticleClose - Article - Close article view
 * @property {"ArticleSwipe"} ArticleSwipe - Article - Swipe to previous or next article
 * @property {"ArticleListScrollView"} ArticleListScrollView - Article list - Scroll list of articles
 * @property {"ArticleListCellClick"} ArticleListCellClick - Article list - List item click
 * @property {"IssueOverviewScrollView"} IssueOverviewScrollView - Issue overview - Scroll issue overview
 * @property {"IssueOverviewPageClick"} IssueOverviewPageClick - Issue overview - Page click
 * @property {"PrintDownloadOverviewScrollView"} PrintDownloadOverviewScrollView - Print/download issue overview - Scroll view
 * @property {"PrintDownloadOverviewPageSelect"} PrintDownloadOverviewPageSelect - Print/download issue overview - Page select
 * @property {"PrintDownloadOverviewPageDeselect"} PrintDownloadOverviewPageDeselect - Print/download issue overview - Page deselect
 * @property {"PrintDownloadOverviewPrintButtonClick"} PrintDownloadOverviewPrintButtonClick - Print/download issue overview - Print button click
 * @property {"PrintDownloadOverviewDownloadButtonClick"} PrintDownloadOverviewDownloadButtonClick - Print/download issue overview - Download button click
 * @property {"ScrubberPageClick"} ScrubberPageClick - Scrubber - Page click
 * @property {"AdView"} AdView - Ad - Ad view
 * @property {"AdShareButtonClick"} AdShareButtonClick - Ad - Share button click
 * @property {"AdClose"} AdClose - Ad - Close ad view
 * @property {"AdShareOpened"} AdShareOpened - Ad - Share opened
 * @property {"AdPrintButtonClick"} AdPrintButtonClick - Ad - Print button click
 */


/**
 * Set the language for the webreader. Default is 'en' for the language.
 * @param {string} lang language code. Default options are 'en', 'de', 'fr', 'es'
 * @param {Messages} [messages] messages for the language of type {@link module:Webreader~Messages Messages}
 */
export function setLanguageMessages(lang, messages) {
  if (lang === undefined) {
    console.error('language code is required - set language failed!')
  }
  if (messages !== undefined) {
    i18n.global.mergeLocaleMessage(lang, messages)
  }

  i18n.global.locale = lang
}

/**
 * Language messages for the given language code.
 * @param {string} [lang]  - language code for the messages
 * @returns {Messages} language messages of type {@link module:Webreader~Messages Messages}
 */
export function getLanguageMessages(lang) { 
  if (lang === undefined) {
    lang = i18n.global.locale
  }
  return i18n.global.getLocaleMessage(lang)
}

/**
 * Get the current language for the webreader.
 * @returns {string} language code
 */ 
export function getLanguage() {
  return i18n.global.locale
}

/**
 * unmount the webreader app and optionally call a callback function after unmounting is complete.
 * @param {*} callback - optional callback function to be called after unmounting is complete.
 */
export function unmount(callback) {
  if (vueApp !== null) {
    // Store the callback to be called after unmounting is complete
    if (callback && typeof callback === 'function') {
      vueApp.config.globalProperties.$unmountCallback = callback
    }
    vueApp.unmount()
  } else if (callback && typeof callback === 'function') {
    // If vueApp is null, call callback immediately
    callback()
  }
}

/**
 * Set an option for the webreader. @see {@link module:Webreader~Options|Options} for available options.
 * @param {string} key key of the option to set
 * @param {any} value value of the option to set
 */
export function setOption(key, value) {
  validateSetOption(key)
  vueApp.config.globalProperties.$readerOptions[key] = value
}

/**
 * Get the URL for the interstitial info endpoint
 * @returns {string} URL for the interstitial info endpoint
 */
export function getInterstitialInfoURL() {
  const pubPath = vueApp.config.globalProperties.$opsURL.split('issues')[0]
  return pubPath + 'interstitialInfo'
}

/**
 * Set the article font size.
 * @param {number} fontSize 
 */
export function setArticleFontSize(fontSize) {
  if (vueApp !== null) {
    // check if fontSize is a valid number and is not a string or NaN
    if (typeof fontSize !== 'number' || isNaN(fontSize) || fontSize <= 0) {
      console.error('Invalid font size. It should be a positive number.')
      return
    }

    vueApp.config.globalProperties.$store.state.articleFontsize = fontSize
  } else {
    console.error('vueApp is not initialized. Cannot set article font size.')
  }
}

/**
 * Set the favourite articles.
 * @param {Array} favouriteArticles Array of article IDs that are marked as favourite.
 */
export function setFavouriteArticles(favouriteArticles) {
  if (vueApp !== null) {
    vueApp.config.globalProperties.$store.commit('setFavArticles', favouriteArticles)
  } else {
    console.error('vueApp is not initialized. Cannot set article font size.')
  }
}

/**
 * Current options for the webreader
 * @returns {Options} options of type {@link module:Webreader~Options Options}
 */
export function getOptions() {
  if (vueApp !== null) {
    return vueApp.config.globalProperties.$readerOptions
  }
  return {
    enableArticleSocialSharing: true,
    enableFullscreen: true,
    enableFontSizeChange: true,
    enableArticlePrinting: true,
    enableDownloadOfArticleImage: true,
    enableDownloadOfPages: true,
    enablePrintOfPages: true,
    enablePrintOfCurrentPage: false,
    enablePrintDownloadConsent: false,
    enableArticleList: true,
    enableHomeNavigation: true,
    enableIssueOverview: true,
    enableCropPage: true,
    enableSectionsBar: true,
    enableScrubber: true,
    enablePageViewModeChange: true,
    enableArticlesWithTablesAsImages: false,
    enablePageAds: true,
    enableZoomButtonsForMobile: false,
    enableOnlineURLArticles: false,
    enableInteractionLayerHoverEffect: true,
    enableArticleFavourite: false,
    enablePagePreviewInArticleList: true,
    enablePageNumbersInArticleList: true,
    enableSectionPageNumbering: false,
    enableVerticalLayout: false,
    enableVerticalLayoutForMobile: false,
    enableArticleViewModeChange: true,
    enableUniquePageGroups: true,
    enableArticleAudioAutoplayNext: true,
    enablePDFTextLayerForDesktop: false,
    enableArticles: true,
    enableArticleNavigation: true,
    enableInterstitialAds: false,
    enableSinglePagesModeSwitch: true,
    maxZoom: 6.0,
    doubleClickZoomIncrease: 3.0,
    navigationBarTitle: "",
    homeNavigationUrl: "",
    articleSharingType: "print", // print, online, reader, custom
    articleViewModeDefault: "text", // text, image
    singlePagesModeDefault: false, // false for double pages mode, true for single pages mode
    language: "en", // en, de, fr, es
    isFullPageViewMode: true,
    pagePDFQuality: 2.0,
    pageCropScale: 4,
    startPage: 1,
    startArticleId: "",
    useBitmapPagesForDesktop: false,
    useBitmapPagesForMobile: true,
    routerMode: "hash", // hash, abstract
    customArticleShareURLFunction: getCustomShareURL, // function to return custom share URL if articleSharingType is 'custom'
    interstitialAdFunctions: {
      wantInterstitialAd: wantInterstitialAd,
      displayInterstitialAd: displayInterstitialAd,
      interstitialAdPageIsReady: interstitialAdPageIsReady,
      interstitialAdPageWillBeDestroyed: interstitialAdPageWillBeDestroyed
    }, // object with functions for interstitial ads: wantInterstitialAd, displayInterstitialAd, interstitialAdPageIsReady, and interstitialAdPageWillBeDestroyed
  }  
}

/**
 * Event data related to the event
 * @typedef {Object} EventData
 * @property {any} attributes - Attributes related to the event. It can be attributes of the article, page, issue, etc.
 * @property {EventContext} context - The context of the event. For example 'PageView', 'ArticleView', etc.
 * @property {EventAction} action - The event action. For example 'PagesPageView', 'ArticleView', etc.
 * @property {string} label - label of the event. It can be the article title, page number, etc. May be undefined.
 * @property {number} value - value of the event. It is a number for example the zoom level, etc. May be undefined.
 * @property {string} extraInfo - Additional information related to the event. May be undefined.
 * @property {string} orientation - The orientation of the event. For example 'Portrait', 'Landscape'.
 *  
 */

/**
 * Event callback function
 * @callback EventCallback
 * @param {EventData} eventData - The data related to the event.
 */

/**
 * Add an event listener for the given event action
 * @param {EventAction} action 
 * @param {EventCallback} callback 
 */

/** 
 * Event actions for the webreader
 * @returns {Array<EventAction>} event actions of type {@link module:Webreader~EventAction EventAction}
 */
export function getEventActions() {
  return [
    'AllActions',
    'IssueLoaded',
    'PagesScrubberButtonClick', 
    'PagesFullscreenModeOnButtonClick', 
    'PagesFullscreenModeOffButtonClick',
    'PagesPageView',
    'PagesInterstitialPageView',
    'PagesClickableAd',
    'PagesPageZoomDoubleClickOut',
    'PagesPageZoomDoubleClickIn',
    'PagesPageZoomTouchPinchOut',
    'PagesPageZoomTouchPinchIn',
    'PagesPageZoomTrackpadPinchOut',
    'PagesPageZoomTrackpadPinchIn',
    'PagesPageZoomButtonClickOut',
    'PagesPageZoomButtonClickIn',
    'PagesCropButtonClick',
    'PagesIssueOverviewButtonClick',
    'PagesIssueDownloadButtonClick',
    'PagesIssuePrintButtonClick',
    'PagesArticleListButtonClick',
    'PagesGroupNavigationSelect',
    'PagesPrintCurrentPageButtonClick',
    'PagesSinglePagesModeButtonClick',
    'PagesDoublePagesModeButtonClick',
    'PagesArticleClick',
    'PagesSwipe',
    'CropOkButtonClick',
    'CropCancelButtonClick',
    'ArticleView',
    'ArticleShareButtonClick',
    'ArticleShareOpened',
    'ArticleShareCancelled',
    'ArticleIncreaseFontSizeButtonClick',
    'ArticleDecreaseFontSizeButtonClick',
    'ArticlePrintButtonClick',
    'ArticleAddToFavouritesButtonClick',
    'ArticleRemoveFromFavouritesButtonClick',
    'ArticleClose',
    'ArticleSwipe',
    'ArticleListScrollView',
    'ArticleListCellClick',
    'IssueOverviewScrollView',
    'IssueOverviewPageClick',
    'PrintDownloadOverviewScrollView',
    'PrintDownloadOverviewPageSelect',
    'PrintDownloadOverviewPageDeselect',
    'PrintDownloadOverviewPrintButtonClick',
    'PrintDownloadOverviewDownloadButtonClick',
    'ScrubberPageClick'
  ]
}

/**
 * Add an event listener for the given event action.
 * @param {EventAction} action - The event action to listen for.
 * @param {EventCallback} callback - The callback function to invoke when the event occurs.
 */
export function on(action, callback) {
  if (!getEventActions().includes(action)) {
    console.error('Event action is not supported: ' + action);
    return;
  }

  if (!vueApp.config.globalProperties.$cciTrackingModule.eventListeners[action]) {
    vueApp.config.globalProperties.$cciTrackingModule.eventListeners[action] = [];
  }
  vueApp.config.globalProperties.$cciTrackingModule.eventListeners[action].push(callback);
}

/**
 *  Determine if interstitial ads should be displayed on previous and/or next pages
 * @param {number} swipeCount how many swipes have been made since the last interstitial ad
 * @param {Array<string>} curPageNos current page numbers 
 * @param {Array<string>} prevPageNos previous page numbers
 * @param {string} pageGroup page group
 * @returns {InterstitialAdPlacements} placements for interstitial ads of type {@link module:Webreader~InterstitialAdPlacements InterstitialAdPlacements}
 */
function wantInterstitialAd(swipeCount, curPageNos, prevPageNos, pageGroup) {
  console.error('wantInterstitialAd not implemented');
  return { insertBeforePage: false, insertAfterPage: false };
}

/**
 * @typedef {Object} InterstitialAdPlacements
 * @property {boolean} insertBeforePage - Whether to insert the ad before the page.
 * @property {boolean} insertAfterPage - Whether to insert the ad after the page.
 */

/**
 *  Display the interstitial ad in the given container element
 * @param {string} containerElementId id of the container element
 */
function displayInterstitialAd(containerElementId) {
  console.error('displayInterstitialAd not implemented');
}

/**
 * The interstitial ad page is ready
 * @param {string} containerElementId id of the container element
 * @param {number} maxWidth maximum width of the ad
 * @param {number} maxHeight maximum height of the ad
 * @param {Array<string>} afterPageNos page numbers after which the ad is ready
 */
function interstitialAdPageIsReady(containerElementId, maxWidth, maxHeight, afterPageNos) {
  console.error('interstitialAdPageIsReady not implemented');
}
/**
 * The interstitial ad page will be destroyed
 * @param {string} containerElementId id of the container element
 */
function interstitialAdPageWillBeDestroyed(containerElementId) {
  console.error('interstitialAdPageWillBeDestroyed not implemented');
}

/**
 * Get the custom share URL for the given article object
 * @param {Object} articleObj article object
 * @returns {string} custom share URL
 */
function getCustomShareURL(articleObj) {
  console.error('getCustomShareURL not implemented');
  return '';
}

function validateOptions(options) {
    // check if custom share URL function is defined when articleSharingType is custom
    if (options.articleSharingType === 'custom' && options.customArticleShareURLFunction === undefined) {
      console.error('customArticleShareURLFunction is required when articleSharingType is custom')
    }
    // check if articleSharingType is one of the supported types
    if (options.articleSharingType !== 'custom' && options.articleSharingType !== 'print' && options.articleSharingType !== 'online' && options.articleSharingType !== 'reader') {
      console.error('articleSharingType must be one of the following: custom, print, online, reader')
    }
  
    // check if routerMode is one of the supported types
    if (options.routerMode !== 'hash' && options.routerMode !== 'abstract') {
      console.error('routerMode must be one of the following: hash, abstract')
    }

    // check if language is one of the supported types
    if (options.language !== 'en' && options.language !== 'de' && options.language !== 'fr' && options.language !== 'es') {
      console.error('language must be one of the following: en, de, fr, es')
    }
}

function validateSetOption(key) {
  if (vueApp.config.globalProperties.$readerOptions[key] === undefined) {
    console.error('Option not found: ' + key + ' - set option failed!')
  }

  // Some options cannot be set at runtime
  const invalidOptions = ['enableVerticalLayout', 'enableVerticalLayoutForMobile'];
  if (invalidOptions.includes(key)) {
    console.error('setOption not supported for ' + key + '. Set the option in the options object when loading the webreader.');
  }
}

/**
 * Load the webreader with the given issue URL and options
 * @param {string} issueUrl API endpoint for the issue 
 * @param {Options} [options] Options for the webreader. See getOptions() for available options. 
 */
export function loadWebreader(issueUrl, options) {
  let inputOptions = options !== undefined ? options : {}

  let defaultOptions = getOptions()

  compareKeys(defaultOptions, inputOptions)

  function compareKeys(object1, object2) {
    // Get the keys of object1 and object2
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    // Iterate over keys of object2
    keys2.forEach(key => {
        // Check if the key exists in object1
        if (!keys1.includes(key)) {
          console.log(`Option key '${key}' is not a supported option key.`)
        }
    });
  }

  let mergedOptions = Object.assign({}, defaultOptions, inputOptions)

  validateOptions(mergedOptions)

  if (mergedOptions.useBitmapPagesForDesktop !== undefined && isDesktop()) {
    mergedOptions.useBitmapPages = mergedOptions.useBitmapPagesForDesktop
  } else if (mergedOptions.useBitmapPagesForMobile !== undefined && !isDesktop()) {
    mergedOptions.useBitmapPages = mergedOptions.useBitmapPagesForMobile
  }

  let decodedIssueUrl = decodeURIComponent(issueUrl)
  const issueOpsURL = decodedIssueUrl.slice(-1) === '/' ? decodedIssueUrl + 'OPS/' : decodedIssueUrl + '/OPS/'

  //  If the routerMode is not hash or abstract, then we set it to hash
  if (mergedOptions.routerMode !== 'hash' && mergedOptions.routerMode !== 'abstract') {
    mergedOptions.routerMode = 'hash'
  }

  if (mergedOptions.routerMode === 'hash') {
    // If the page is reloaded and the hash has #/error, then we redirect to the first page.
    const url = new URL(window.location.href)
    if (url.hash.indexOf('#/error') !== -1) {
      if (window.history.replaceState) {
        url.hash = '#/pages/1'
        window.history.replaceState('', '', url.href)
      }
    }
  }

  if (!mergedOptions.useBitmapPages) {
    injectPdfJSLibrary(store)
  }

  injectHammerJSLibrary()

  const history = mergedOptions.routerMode === 'hash' ? createWebHashHistory() : createMemoryHistory()

  const routes = [
    { path: '/', name: 'home', component: CciLoader },
    { path: '/pages/:pageNr', name: 'pages', component: CciPages },
    { path: '/articles/:articleNr', name: 'articles', component: CciArticleContainer },
    { path: '/articles-mobile', name: 'articles-mobile', component: CciArticleListMobile },
    { path: '/article-mobile/:articleNr', name: 'article-mobile', component: CciArticle },
    { path: '/error/:message', name: 'error', component: CciError, props: true }
  ]

  const router = createRouter({
    history: history,
    routes: routes
  })

  vueApp = createApp({
    template: '<WebreaderApp/>',
    components: { WebreaderApp }
  })

  i18n.global.locale = mergedOptions.language

  // Add the plugins to the app
  vueApp.use(Vue3TouchEvents)
  vueApp.use(Vue3SocialSharingPlugin)
  vueApp.use(store)
  vueApp.use(router)
  vueApp.use(i18n)
  vueApp.use(CciEventTrackingPlugin)

  const axiosInstance = axios.create()
  // Add a request interceptor
  axiosInstance.interceptors.request.use(config => {
    NProgress.start()
    return config
  }, error => {
    NProgress.done()
    return Promise.reject(error)
  })

  // Add a response interceptor
  axiosInstance.interceptors.response.use(response => {
    NProgress.done()
    return response
  }, error => {
    NProgress.done()
    return Promise.reject(error)
  })

  // Global properties for the app
  vueApp.config.globalProperties.$axios = axiosInstance
  vueApp.config.globalProperties.$opsURL = issueOpsURL
  vueApp.config.globalProperties.$readerOptions = reactive(mergedOptions)
  vueApp.config.globalProperties.$injectPdfJSLibrary = injectPdfJSLibrary

  // Set the whitespace option to 'preserve' to keep the whitespace in the template
  vueApp.config.compilerOptions.whitespace = 'preserve'

  // add tracking plugin to the store
  store.$cciEventTrackingPlugin = vueApp.config.globalProperties.$cciEventTrackingPlugin

  vueApp.mixin({
    computed: {
      isPortrait () {
        // isPortrait is set in store by the didRize event in CciPages.vue
        // We need to check if it is undefined, because the first time the app is loaded, the isPortrait is not set.
        // We do this to trigger the computed properties that depend on isPortrait.
        return store.state.isPortrait === undefined ? window.matchMedia('(orientation: portrait)').matches : store.state.isPortrait
      },
      isTouchBrowser () {
        var prefixes = ' -webkit- -moz- -o- -ms- '.split(' ')
        var mq = function (query) {
          return window.matchMedia(query).matches
        }
        /* eslint-disable no-undef */
        if (('ontouchstart' in window) || (window.DocumentTouch && document instanceof DocumentTouch)) {
          return true
        }
        /* eslint-enable no-undef */
        // include the 'heartz' as a way to have a non matching MQ to help terminate the join
        // https://git.io/vznFH
        var query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('')
        return mq(query)
      },
      isSafari () {
        let userAgent = navigator.userAgent || navigator.vendor || window.opera
        return /Safari/i.test(userAgent) && userAgent.indexOf('Chrome') === -1
      },
      isDesktop () {
        let userAgent = navigator.userAgent || navigator.vendor || window.opera
        let androidOrIos = /android/i.test(userAgent) || (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
        if (androidOrIos) {
          return false
        }
        return !(this.isTouchBrowser && this.isSafari);
      },
      isPhone () {
        return !this.isDesktop && ((!this.isPortrait && window.innerHeight < 450) || (window.innerWidth < 550 && this.isPortrait))
      },
      isPhonePortrait () {
        return this.isPhone && this.isPortrait
      },
      isPhoneLandscape () {
        return this.isPhone && !this.isPortrait
      },
      isTablet () {
        return !this.isDesktop && !this.isPhone
      },
      isTabletPortrait () {
        return this.isTablet && this.isPortrait
      },
      isTabletLandscape () {
        return this.isTablet && !this.isPortrait
      },
      isSinglePages () {
        return this.isPhone || (this.isTabletPortrait && !IssueData.gptAdsEnabled || this.isPortrait || !store.state.pageViewMode || IssueData.isSinglePages)
      },
      isVerticalLayout () {
        if (this.$readerOptions.enableVerticalLayout) {
          return true
        }
        if (this.$readerOptions.enableVerticalLayoutForMobile) {
          return (this.isPhone || this.isTabletPortrait)
        }
        return false
      },
    },
    methods: {
      getEncodedResourceUrl: function (resource, revTimeStamp) {
        if (resource === null) {
          return ''
        }
        if (resource.indexOf('Public/') !== -1) {
          let refToEncode = resource.split('Public/')[1]
          return this.$opsURL + 'Public/' + encodeURIComponent(refToEncode) + (refToEncode.indexOf('?') > 0 ? '&' : '?') + 'rev=' + revTimeStamp
        }
        return this.$opsURL + encodeURIComponent(resource) + (resource.indexOf('?') > 0 ? '&' : '?') + 'rev=' + revTimeStamp
      },
      getResourceUrl: function (resource, revTimeStamp) {
        if (resource === null) {
          return ''
        }
        if (resource.indexOf('Public/') !== -1) {
          let refToEncode = resource.split('Public/')[1]
          return this.$opsURL + 'Public/' + refToEncode + (refToEncode.indexOf('?') > 0 ? '&' : '?') + 'rev=' + revTimeStamp
        }
        return this.$opsURL + resource + (resource.indexOf('?') > 0 ? '&' : '?') + 'rev=' + revTimeStamp
      },
      async requestUrlTest (url, accept400, mode) {
        const options = mode !== undefined ? { method: 'HEAD', mode: mode } : { method: 'HEAD' }
        return fetch(url, options).then(response => {
          if (accept400) {
            return response.ok || response.status === 400
          }
          if (mode === 'no-cors') {
            return response.ok || response.status === 0
          }
          return response.ok
        }).catch(() => {
          return false
        })
      },
      getWidthOfWidestPage: function (singlePages, height) {
        let widestPage = singlePages[0]
        for (var i = 0; i < singlePages.length; i++) {
          const pageWidth = singlePages[i].isSpread ? parseFloat(singlePages[i].attributes.ObjectWidth / 2) : parseFloat(singlePages[i].attributes.ObjectWidth)
          const widestPageWidth = widestPage.isSpread ? parseFloat(widestPage.attributes.ObjectWidth / 2) : parseFloat(widestPage.attributes.ObjectWidth)
          if (pageWidth > widestPageWidth) {
            widestPage = singlePages[i]
          }
        }
        const pageObjHeight = parseFloat(widestPage.attributes.ObjectHeight)
        const pagesObjWidth = widestPage.isSpread ? parseFloat(widestPage.attributes.ObjectWidth / 2) : parseFloat(widestPage.attributes.ObjectWidth)
  
        const heightFactor = height / pageObjHeight
        const itemWidth = (pagesObjWidth * heightFactor)
        return itemWidth
      },
      getScrollBarWidth: function () {
        var inner = document.createElement('p')
        inner.style.width = "100%"
        inner.style.height = "200px"
  
        var outer = document.createElement('div')
        outer.style.position = "absolute"
        outer.style.top = "0px"
        outer.style.left = "0px"
        outer.style.visibility = "hidden"
        outer.style.width = "200px"
        outer.style.height = "150px"
        outer.style.overflow = "hidden"
        outer.appendChild(inner);
  
        document.body.appendChild(outer)
        var w1 = inner.offsetWidth
        outer.style.overflow = 'scroll'
        var w2 = inner.offsetWidth
        if (w1 == w2) w2 = outer.clientWidth
  
        document.body.removeChild(outer)
  
        return (w1 - w2)
      },
      getOnlineUrl: function (articleObj) {
        const onlineURL = articleObj.attributes.OnlineURL
        if (onlineURL !== undefined && onlineURL !== '' && typeof interceptArticleOnlineURL === 'function') {
          return interceptArticleOnlineURL(onlineURL)
        }
        return onlineURL
      },
      getShareURL: function (pageChildObj) {
        switch(this.$readerOptions.articleSharingType) {
          case 'print':
            if (store.state.articleViewMode === 'image' || pageChildObj.html === undefined || this.isTablesAsImages(pageChildObj)) {
              const imgFormat = this.getImageFormat(pageChildObj.page.background)
              const imgFile = pageChildObj.id + imgFormat
              return this.$opsURL + imgFile + '?cropFromPage=true'
            }
  
            var onlineURL = this.getOnlineUrl(pageChildObj)
            if (this.$readerOptions.enableOnlineURLArticles && onlineURL !== undefined && onlineURL !== '') {
              return onlineURL
            }
            return this.$opsURL + pageChildObj.html
          case 'online':
            return this.getOnlineUrl(pageChildObj)
          case 'reader': {
            var href = window.location.href
            const pagesIndex = href.indexOf('#/pages/')
            if (pagesIndex !== -1) {
              href = href.substring(0, pagesIndex)
              var pageQuery = pageChildObj.page.pageNumber
              if (pageChildObj.page.isSpread) {
                pageQuery = pageQuery.split('-')[0]
              }
              return href + '&page=' + pageQuery + '&article=' + pageChildObj.id
            }
            return href
          }
          case 'custom': {
            return this.$readerOptions.customArticleShareURLFunction(pageChildObj)
          }
          default: {
            return undefined
          }
        }
      },
      getAudioMp3UrlEndpoint: function () {
        let issueWSUrl = this.$opsURL
        issueWSUrl = issueWSUrl.substr(0, issueWSUrl.lastIndexOf('/'))
        issueWSUrl = issueWSUrl.substr(0, issueWSUrl.lastIndexOf('/'))
        return issueWSUrl + '/getAudioMP3'
      },
      getArticleMp3Url: function (articleId) {
        const that = this
        return new Promise(function (resolve, reject) {
          const promise = fetch(that.getAudioMp3UrlEndpoint() + '?articleId=' + articleId)
          promise.then(async response => {
            const json = await response.json()
  
            if (!response.ok) {
              const error = (json && json.message) || response.statusText
              reject(error)
            }
            resolve(json.audioMp3Url)
          }).catch(error => {
            console.error('There was an error!', error)
            reject(error)
          })
        })
      },
      getJumpStoryArticleImages: function(article, imgUrls) {
        if (article.attributes.JumpToPage !== undefined && article.attributes.JumpToPage !== '' && article.attributes.JumpTo !== undefined && article.attributes.JumpTo !== '') {
          let pageId = article.attributes.JumpToPage
          let articleId = article.attributes.JumpTo.split(':')[1]
          for (var i = 0; i < store.state.articles.length; i++) {
            const art = store.state.articles[i]
            if (art.id === articleId && art.page.id === pageId) {
              // found next article
  
              // get image URL
              const imgFormat = this.getImageFormat(art.page.background)
              const articleImgFile = art.id + imgFormat
              const articleImgURL = this.getEncodedResourceUrl(articleImgFile, art.attributes.LastRev) + '&cropFromPage=true'
              imgUrls[articleId] = articleImgURL
  
              this.getJumpStoryArticleImages(art, imgUrls)
            }
          }
        }
        return imgUrls
      },
      createPdfWorker () {
        if (this.$pdfWorker) { 
          return this.$pdfWorker
        }
        const workerParams = {
          verbosity: 1,
          port: this.$pdfjsLib.GlobalWorkerOptions.workerPort
        }
        
        this.$pdfWorker = workerParams.port ? this.$pdfjsLib.PDFWorker.fromPort(workerParams) : new this.$pdfjsLib.PDFWorker(workerParams)
        return this.$pdfWorker
      },
      printImg (imgUrl) {
        // Create a hidden iframe element
        const iframePrint = document.createElement('iframe');
        iframePrint.style.display = 'none';
        document.body.appendChild(iframePrint);
        
        // Get the document object of the iframe
        const doc = iframePrint.contentWindow.document;
        doc.open(); // Open the document for writing
        // Write the HTML content to the document, including the image source
        doc.write('<!DOCTYPE html><html><head><title>Print Image</title></head><body><img src="' + imgUrl + '"></body></html>');
        doc.close(); // Close the document after writing
        
  
        // Wait for a short delay for the iframe content to load, then print
        setTimeout(function() {
          iframePrint.contentWindow.print();
  
          // Remove the iframe after a delay (e.g., 2 seconds)
          setTimeout(function() {
            document.body.removeChild(iframePrint);
          }, 2000);
        }, 500);
      },
      downloadImg (object) {
        let imgFormat
        let url
        if (object.pdf !== undefined && object.pdf !== null) {
          imgFormat = '.pdf'
          url = this.getEncodedResourceUrl(object.pdf, object.attributes.LastRev)
        } else {
          imgFormat = this.getImageFormat(object.page.background)
          const articleImgFile = object.id + imgFormat
          url = this.getEncodedResourceUrl(articleImgFile, object.attributes.LastRev) + '&cropFromPage=true'
        }
  
        let link = document.createElement('a')
        link.href = url
        link.download = IssueData.publicationName + '_' + IssueData.issueName + '_' + object.id + imgFormat
        document.body.appendChild(link)
        link.click()
      },
      routeTo (routeName, params) {
        if (this.$router.currentRoute.value.name === routeName && this.$router.currentRoute.value.params === params) {
          return
        }

        let isOnRoute = false

        switch (routeName) {
          case 'pages':
            isOnRoute = this.$router.currentRoute.value.name === 'pages' && this.$router.currentRoute.value.params.pageNr === params.pageNr
            break
          case 'articles':
            isOnRoute = this.$router.currentRoute.value.name === 'articles' && this.$router.currentRoute.value.params.articleNr === params.articleNr
            break
          case 'articles-mobile':
            isOnRoute = this.$router.currentRoute.value.name === 'articles-mobile'
            break
          case 'article-mobile':
            isOnRoute = this.$router.currentRoute.value.name === 'article-mobile' && this.$router.currentRoute.value.params.articleNr === params.articleNr
            break
          case 'error':
            isOnRoute = this.$router.currentRoute.value.name === 'error' && this.$router.currentRoute.value.params === params
            break
        }

        if (!isOnRoute) {
          this.$router.push({ name: routeName, params: params })
        }
      },
      isDisplayingDoublePages: function () {
        for (const page of store.state.pages) {
          if (page.secondPage !== null) {
            return true
          }
        }
        return false
      },
      getImageFormat(url) {
        // Extract the file extension using a regular expression
        var match = url.match(/\.([^.]+)$/);
        if (match) {
          // Return the matched extension including the dot
          return match[0].toLowerCase();
        }
        // If no match found, return png as default
        return '.png';
      },
      isPagesInFocus () {
        return !store.state.showArticleModal 
        && !store.state.showIssueOverview 
        && !store.state.showScrubber
        && this.$router.currentRoute.value.name === 'pages'
      },
      skipToMainContent () {
        store.commit('closeAllModals')
        if (this.isPagesInFocus()) {
          const pagesContainer = document.getElementById('pages-container');
          if (pagesContainer !== null) {    
            // Temporarily set tabindex to -1 to make the container focusable
            pagesContainer.setAttribute('tabindex', '-1');
  
            // The next part is to make sure that we only change tabindex
            // of the child elements that are visible (on the current page)
            const currentPage = store.state.currentPage
            let focusableElements = []
  
            const articles = currentPage.firstPage.articles.concat(currentPage.secondPage ? currentPage.secondPage.articles : [])
            // article element id example: GH615TH70E.1_46 (article id + a number)
            for (const article of articles) {
              // get elements with id that contains article id and add them to focusableElements
              const articleElements = pagesContainer.querySelectorAll('[id*="' + article.id + '"]');
              focusableElements = focusableElements.concat(Array.from(articleElements));
            }
  
            const clickableAds = currentPage.firstPage.clickableAds.concat(currentPage.secondPage ? currentPage.secondPage.clickableAds : [])
            for (const clickableAd of clickableAds) {
              // get elements with id that contains ad id and add them to focusableElements
              const clickableAdElements = pagesContainer.querySelectorAll('[id*="' + clickableAd.id + '"]');
              focusableElements = focusableElements.concat(Array.from(clickableAdElements));
            }
  
            const ads = currentPage.firstPage.ads.concat(currentPage.secondPage ? currentPage.secondPage.ads : [])  
            for (const ad of ads) {
              // get elements with id that contains zoom object id and add them to focusableElements
              const adElements = pagesContainer.querySelectorAll('[id*="' + ad.id + '"]');
              focusableElements = focusableElements.concat(Array.from(adElements));
            }
      
            // set tabindex to -1 for all focusable elements
            focusableElements.forEach(element => {
              element.setAttribute('tabindex', '-1');
            });
      
            // Focus on the container
            pagesContainer.focus();
      
            // set tabindex to 0 for all focusable elements after a short delay
            setTimeout(() => {
              focusableElements.forEach(element => {
                element.setAttribute('tabindex', '0');
              });
      
              // Remove tabindex from the container to clean up
              pagesContainer.removeAttribute('tabindex');
            }, 100); // Adjust the delay as needed
          }
        }
        if (this.$router.currentRoute.value.name === 'articles') {
          // this.focusOnArticleView();
          // article-list-article
          const articleMain = document.getElementById('article-list-article');
          if (articleMain !== null) {
            articleMain.setAttribute('tabindex', -1);
            articleMain.focus();
          }
        }
        if (this.$router.currentRoute.value.name === 'article-mobile') {
          this.focusOnArticleView();
        }
        if (this.$router.currentRoute.value.name === 'articles-mobile') {
          const articleList = document.getElementById('article-list-mobile-container');
          if (articleList !== null) {
            articleList.setAttribute('tabindex', -1);
            articleList.focus();
            setTimeout(() => {
              // Remove tabindex from the container to clean up
              articleList.removeAttribute('tabindex');
            }, 100);
          }
        }
      },
      focusOnArticleView () {
      // get article iframe or article img - depending on display none
        let articleIframe
        let articleImg
        if (store.state.currentArticle !== undefined) {
          articleIframe = document.getElementById(store.state.currentArticle.id + '-article')
          articleImg = document.getElementById(store.state.currentArticle.id + '-article-img')
        }

        if (!articleIframe) {
          articleIframe = document.getElementById(store.state.selectedPageChild.id + '-article-modal')
        }

        if (!articleImg) {
          articleImg = document.getElementById(store.state.selectedPageChild.id + '-article-modal-img')
        }

        if (articleIframe.style.display !== 'none') {
          try {
            // Focus on the iframe element first to ensure it receives focus
            const iframeDoc = articleIframe.contentDocument || articleIframe.contentWindow.document
            iframeDoc.body.children[0].focus() // Focus on the first element in the iframe body
          } catch (err) {
            console.log('Error focusing on article iframe element:', err);
          }
          
          // try {
          //   // call focusOnArticle function in iframe
          //   console.log('try Focusing on article iframe function focusOnArticle')
          //   articleIframe.contentWindow.focusOnArticle()
          // }
          // catch (err) {
          //   // focus on iframe if focusOnArticle function is not available
          //   console.log('Error, focusing on article iframe')
          //   articleIframe.contentWindow.focus()
          // }
        } else if (articleImg.style.display !== 'none') {
          articleImg.focus()
        }
      },
      parseQueryParamsFromUrl(url) {
        // Remove the part before the query string (e.g., "keydownInArticle.cci?")
        const queryString = url.split('?')[1];

        if (!queryString) return {}; // If no query string, return an empty object

        const params = new URLSearchParams(queryString);
        const parsedObject = {};

        // Iterate through each key-value pair in the query string
        params.forEach((value, key) => {
          // Convert boolean-like values to true/false
          if (value === 'true') {
            parsedObject[key] = true;
          } else if (value === 'false') {
            parsedObject[key] = false;
          } else {
            parsedObject[key] = value; // Keep it as is (e.g., keycode as string)
          }
        });

        return parsedObject;
      },
      filterArticlesForArticleList (articles) {
        const filteredArticles = []
        for (const article of articles) {
          // check if article.html is not null and a filteredArticles does not contain an article with the same .html
          if (article.html !== null && filteredArticles.findIndex(a => a.html === article.html && a.page.sections[0].id === article.page.sections[0].id) === -1) {
            // check if article is not a jump story
            if (article.attributes.JumpOriginArticleId === undefined && article.attributes.TeaserText !== '') {
              filteredArticles.push(article)
            }
          }
        }
        return filteredArticles
      },
      isTablesAsImages (article) {
        return this.$readerOptions.enableArticlesWithTablesAsImages && article !== undefined && article.attributes.TableInArticle !== undefined && article.attributes.TableInArticle === '1'
      },
    }
  })

  vueApp.mount('#webreader-app')
}

function injectPdfJSLibrary(store) {
  const browser = getBrowser()

  let isLegacy = false
  if (browser.name === 'Safari' && parseInt(browser.version) < 15) {
    isLegacy = true
  }
  if (browser.name === 'Chrome' && parseInt(browser.version) < 84) {
    isLegacy = true
  }
  if (browser.name === 'Firefox' && parseInt(browser.version) < 90) {
    isLegacy = true
  }
  if (browser.name === 'Opera' && parseInt(browser.version) < 70) {
    isLegacy = true
  }
  if (browser.name === 'Edg' && parseInt(browser.version) < 84) {
    isLegacy = true
  }

  const pdfJsVersion = '3.7.107'
  const head = document.getElementsByTagName('head')[0]
  const scriptElem = document.createElement('script')
  scriptElem.type = 'text/javascript'
  scriptElem.onload = function () {
    let pdfjsLibInit = window['pdfjs-dist/build/pdf']
    pdfjsLibInit.GlobalWorkerOptions.workerSrc = isLegacy ? 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + pdfJsVersion + '/legacy/build/pdf.worker.min.js' : 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + pdfJsVersion + '/build/pdf.worker.min.js'
    // store.state.pdfjsLib = pdfjsLibInit
    vueApp.config.globalProperties.$pdfjsLib = pdfjsLibInit
    var event1 = new CustomEvent('pdfjsloaded')
    const webreaderApp = document.getElementById('webreader-app')
    webreaderApp.dispatchEvent(event1)
  }
  scriptElem.src = isLegacy ? 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + pdfJsVersion + '/legacy/build/pdf.min.js' : 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + pdfJsVersion + '/build/pdf.min.js'
  head.appendChild(scriptElem)
}

function injectHammerJSLibrary() {
  if (isTouchBrowser()) {
    // Swipe of pages only on touch devices
    const head = document.getElementsByTagName('head')[0]
    const scriptElem = document.createElement('script')
    scriptElem.type = 'text/javascript'
    scriptElem.src = 'https://cdnjs.cloudflare.com/ajax/libs/hammer.js/2.0.8/hammer.min.js'
    head.appendChild(scriptElem)
  }
}

function isTouchBrowser() {
  var prefixes = ' -webkit- -moz- -o- -ms- '.split(' ')
  var mq = function (query) {
    return window.matchMedia(query).matches
  }
  /* eslint-disable no-undef */
  if (('ontouchstart' in window) || (window.DocumentTouch && document instanceof DocumentTouch)) {
    return true
  }
  /* eslint-enable no-undef */
  // include the 'heartz' as a way to have a non matching MQ to help terminate the join
  // https://git.io/vznFH
  var query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('')
  return mq(query)
}

function isSafari() {
  let userAgent = navigator.userAgent || navigator.vendor || window.opera
  return /Safari/i.test(userAgent) && userAgent.indexOf('Chrome') === -1
}

function isDesktop() {
  let userAgent = navigator.userAgent || navigator.vendor || window.opera
  let androidOrIos = /android/i.test(userAgent) || (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)

  if (androidOrIos) {
    return false
  }

  if (isTouchBrowser() && isSafari()) {
    return false
  }
  return true
}

function getBrowser() {
  var ua = navigator.userAgent,
    tem, M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || []
    return {
      name: 'IE',
      version: (tem[1] || '')
    }
  }
  if (M[1] === 'Chrome') {
    tem = ua.match(/\bOPR|Edge\/(\d+)/)
    if (tem != null) {
      return {
        name: 'Opera',
        version: tem[1]
      }
    }
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?']
  if ((tem = ua.match(/version\/(\d+)/i)) != null) {
    M.splice(1, 1, tem[1])
  }
  return {
    name: M[0],
    version: M[1]
  }
}